import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Transforms, Editor } from 'slate'
import { useEditor, ReactEditor } from 'slate-react'
import { isImageUrl, getNodeDeserializer, getParent } from '@udecode/slate-plugins'

import useOnClickOutside from '../../../../hooks/useOnClickOutside';
import { getMongoObjectId } from '../../../../utils'


export const insertImage = (editor, file, options) => {
    const image = {
        type: 'image',
        url: file.url,
        name: file.name,
        width: 150,
        height: 150,
        children: [{ text: '' }],
    };
    Transforms.insertNodes(editor, image, options);
    Transforms.move(editor);
};


export const withImage = (props = {}) => (editor) => {
    editor.withImage = true;
    const { insertData } = editor;
    const uploadFunc = props.handleUpload;
    editor.insertData = (data) => {
        const text = data.getData('text/plain');
        const { files } = data;
        if (files && files.length > 0) {
          for (const file of Array.from(files)) {
            const [mime] = file.type.split('/');
            if (mime === 'image') {
                uploadFunc(file).then((result => {
                    insertImage(editor, result);
                }));
               
            }
          }
        } else if (isImageUrl(text) || text.includes(';base64,')) {
          insertImage(editor, {
            url: text,
            name: '',
            status: 'finish',
          });
        } else {
          insertData(data);
        }
    };
    return editor;
  };

export function renderImageElement(props) {

    if (props.element.type === 'image') { 
      return (
          <ImageElement {...props} />
      );
    }
  
    return undefined;

}

export function deserializeImage(options) {
  const type = 'image';
  return  {
    element: getNodeDeserializer({
      type,
      rules: [{ nodeNames: ['IMG'] }],
      node: (el) => {
          return ({ 
            type: 'image',
            width: el.style.width,
            height: el.style.height,
            url: el.src,
            _id: getMongoObjectId()
          })  
      },
    })
  }; 
}

export const ImagePlugin = () => ({
  renderElement: renderImageElement,
  deserialize: deserializeImage(),
  inlineTypes: ['image'],
  voidTypes: ['image'],
});

export default function ImageElement({ attributes, element, children }) {
  const { t } = useTranslation();
  const resizeContainer = useRef()
  const imageRef = useRef({})
  const eventStateRef = useRef({})
  const [width, setWidth] = useState(element.width || 150);
  const [height, setHeight] = useState(element.height);
  const [isResizable, setIsResizable] = useState(false);
  const editor = useEditor()
  useOnClickOutside(resizeContainer, () => {
    if (isResizable) setSize()
    setIsResizable(false);
  })


  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();
    if (element._id) {
      saveEventState(e);
      document.addEventListener('mousemove', resizing);
      document.addEventListener('mouseup', endResize);
    }
  }

  const handleClick = () => { 
    if (element._id) {
      setIsResizable(true)
    } else {
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(editor, { _id: getMongoObjectId() }, { at: path, match: (n) => n.type === 'image' })
    }
    
  }

  const saveEventState = (e) => {
    // Save the initial event details and container state
    const container = resizeContainer.current;
    const containerBoundingClientRect = container.getBoundingClientRect();
    const containerOffset = { 
      top: containerBoundingClientRect.top + document.body.scrollTop,
      left: containerBoundingClientRect.left + document.body.scrollLeft
    }
    const eventState = eventStateRef.current;

    eventState.containerWidth = parseFloat(getComputedStyle(container, null).width.replace('px', ''))
    eventState.containerHeight = parseFloat(getComputedStyle(container, null).height.replace('px', ''))
    eventState.containerLeft = containerOffset.left; 
    eventState.containerTop = containerOffset.top;
    eventState.mouseX = (e.clientX || e.pageX || e.originalEvent.touches[0].clientX) + document.body.scrollLeft; 
    eventState.mouseY = (e.clientY || e.pageY || e.originalEvent.touches[0].clientY) + document.body.scrollTop;
  
    // This is a fix for mobile safari
    // For some reason it does not allow a direct copy of the touches property
    if (e.originalEvent && typeof e.originalEvent.touches !== 'undefined'){
      eventState.touches = e.originalEvent.touches.map(touch => ({
        clientX: 0+touch.clientX,
        clientY: 0+touch.clientY,
      }));
    }
    eventState.evnt = e;
   
  }

  const resizing = function(e){
    const eventState =  eventStateRef.current;
    const target = eventState.evnt.target;
    const mouse = {}; 
    let width;
    let height;
    const constrain = false;
    const minHeight = 60;
    const minWidth = 60;
    const path = ReactEditor.findPath(editor, element);
    const [parentNode] = getParent(editor, path)

    const maxWidth = 630;
    const maxHeight = ['document_header', 'document_footer'].includes(parentNode?.type) ? 150 : 900;
    
    mouse.x = (e.clientX || e.pageX || e.originalEvent.touches[0].clientX) + document.body.scrollLeft; 
    mouse.y = (e.clientY || e.pageY || e.originalEvent.touches[0].clientY) + document.body.scrollTop; 

    const initialImage =  {
      width: parseFloat(getComputedStyle(imageRef.current, null).width.replace('px', '')),
      height: parseFloat(getComputedStyle(imageRef.current, null).height.replace('px', ''))
    }
    
    if(target.classList.contains('resize-handle-se')){
      width = mouse.x - eventState.containerLeft;
      height = mouse.y  - eventState.containerTop;
    } else if(target.classList.contains('resize-handle-sw')){
      width = eventState.containerWidth - (mouse.x - eventState.containerLeft);
      height = mouse.y  - eventState.containerTop;
    } else if(target.classList.contains('resize-handle-nw')){
      width = eventState.containerWidth - (mouse.x - eventState.containerLeft);
      height = eventState.containerHeight - (mouse.y - eventState.containerTop);
    } else if(target.classList.contains('resize-handle-ne')){
      width = mouse.x - eventState.containerLeft;
      height = eventState.containerHeight - (mouse.y - eventState.containerTop);
    }

    // Optionally maintain aspect ratio
    if(constrain || e.shiftKey){
      height = width / initialImage.width * initialImage.height;
    }

    if(width > minWidth && height > minHeight && width < maxWidth && height < maxHeight){
      setWidth(width)
      setHeight(height)
    }

  }

  const endResize = (e) => {
    e.preventDefault();
    document.removeEventListener('mouseup', endResize);
    document.removeEventListener('mousemove', resizing);

    setSize()
};  

  const setSize = () => {
    const [node, location] = [
      ...Editor.nodes(editor, {
      mode: 'highest',
      match: (n) => {
        const nodeId = n._id;
        return !!nodeId && element._id === nodeId;
      },
      at: [],
    })][0];

    Transforms.setNodes(editor, {
      width,
      height
    }, {
      at: location
    })
  }
  

  return (
    <div className="image-element" {...attributes}>
      <ResizableContainer resizable={isResizable} ref={resizeContainer} contentEditable={false}  onMouseDown={handleMouseDown} onClick={handleClick}>
        <img className="resize-image" ref={imageRef} src={element.url} alt={element.name} style={{width, height}} />
      </ResizableContainer>
      {children}
    </div>
  )
}

const ResizableContainer = React.forwardRef(({ resizable, onMouseDown, onClick, children }, ref) => {
  if (!resizable) {
    return (
      <div onClick={onClick} ref={ref}>
        {children}
      </div>
    )
  }


  return (
    <div className="resize-container" ref={ref} contentEditable={false} onMouseDown={onMouseDown}>
        <div className="resize-handle-nw" />
        <div className="resize-handle-sw" />
        <div className="resize-handle-ne" />
        <div className="resize-handle-se" />
        {children}
    </div>
  )
})